import { GridContainer, GridRow, Icon, ICONS, mixins } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import Heading from '@websrc/App/Components/Heading/Heading'
import { spotWrapperProps } from '@websrc/App/Helpers/spotWrapperProps'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapCampaignGridSpot } from '@websrc/DataMappers/mapSpot'
import { _typography } from 'leds'
import React, { useContext } from 'react'

import { LearnMoreButtonType } from '../Collapse/SpotCollapse'
import * as SC from './SpotCampaignGrid.style'

export interface SpotCampaignGridProps {
  spot: ReturnType<typeof mapCampaignGridSpot>['campaign_grid']
  LearnMoreButton?: LearnMoreButtonType
  imageLazy?: boolean
  isTopOfPage?: boolean
}

export const SpotCampaignGrid = ({
  spot,
  LearnMoreButton,
  imageLazy,
  isTopOfPage,
}: SpotCampaignGridProps) => {
  const { matomoPageTemplate } = useContext(AppProviderContext)

  return (
    <GridContainer>
      <GridRow {...spotWrapperProps(spot.caption)} data-testid={'caption'}>
        {spot.items.map(({ item }, i) => {
          const thumbnail = item.background_image?.thumbnail
          const hasBackground = Boolean(thumbnail?.url)

          const maxRows = spot.settings.number_of_items_pr_row

          const getGridForRows = (targetRows: number) => 12 / Math.min(targetRows, maxRows)

          const tablet = getGridForRows(2)
          const desktop = getGridForRows(3)
          const giant = getGridForRows(4)

          return (
            <SC.StyledGridCol desktop={desktop} giant={giant} key={i} tablet={tablet}>
              <SC.ItemWrapper
                allowEmptyLink={true}
                ctaType={'tertiary'}
                link={item.link}
                noStyles={true}
                url={thumbnail?.url ?? undefined}
              >
                {hasBackground && (
                  <SC.ItemBackgroundImageWrapper>
                    <Asset
                      sizes={'(min-width: 1024px) 350px, (min-width: 768px) 480px, 750px'}
                      srcSetWidths={[350, 480, 750, 900]}
                      thumbnail={thumbnail}
                    />
                  </SC.ItemBackgroundImageWrapper>
                )}
                {item.icon && (ICONS as any)[item.icon] && (
                  <SC.StyledIcon
                    color={hasBackground ? mixins.colors.grayWhite : mixins.colors.grayBlack}
                    icon={(ICONS as any)[item.icon]}
                    size={44}
                  />
                )}
                <Heading
                  headline={item.title}
                  level={isTopOfPage ? 2 : 3}
                  noSpacing={true}
                  size={'l'}
                  style={{
                    marginBottom: '30px',
                    color: hasBackground ? mixins.colors.grayWhite : mixins.colors.grayBlack,
                    textAlign: 'center',
                  }}
                />
                {item.link.cta_text && (
                  <SC.StyledButton
                    ctaType={'tertiary'}
                    hasBackground={hasBackground}
                    link={item.link}
                    onClick={() =>
                      matomo.trackEvent(
                        'engagement',
                        matomoPageTemplate,
                        [item.title],
                        [item.link.cta_text],
                        'button'
                      )
                    }
                  >
                    <Icon icon={ICONS.ARROW_RIGHT} size={16} />
                    <span>{item.link.cta_text}</span>
                  </SC.StyledButton>
                )}
                {item.product_asset && (
                  <SC.ProductImage
                    lazyLoading={imageLazy}
                    sizes={'(min-width: 1024px) 350px'}
                    srcSetWidths={[320, 400, 600, 800, 1200]}
                    {...item.product_asset}
                  />
                )}
              </SC.ItemWrapper>
            </SC.StyledGridCol>
          )
        })}
        {LearnMoreButton && (
          <SC.LearnMoreButtonWrapper>
            <LearnMoreButton position={[spot.caption]} />
          </SC.LearnMoreButtonWrapper>
        )}
      </GridRow>
    </GridContainer>
  )
}
